/**
 * 历史任务页面 - Dashboard 风格
 * 左侧统计面板 + 右侧任务卡片列表
 */
import React, { useState, useEffect } from 'react';
import { Tag, Button, Empty, Spin, Typography, message } from 'antd';
import {
  DownloadOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { getTaskList, downloadFile } from '../services/api';
import DataAnalysisModal from '../components/DataAnalysisModal';
import './HistoryPage.css';

const { Paragraph } = Typography;

const HistoryPage = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisTaskId, setAnalysisTaskId] = useState(null);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const response = await getTaskList(20);
      if (response.success) {
        setTasks(response.tasks);
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        setTasks([]);
        message.error('请先登录');
      } else {
        setTasks([]);
        message.error('加载任务历史失败');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTasks(); }, []);

  const stats = {
    total: tasks.length,
    processing: tasks.filter(t => t.status === 'processing').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    failed: tasks.filter(t => t.status === 'failed').length,
  };

  const filteredTasks = filter
    ? tasks.filter(t => t.status === filter)
    : tasks;

  const getStatusTag = (status) => {
    const map = {
      uploaded: { color: 'default', text: '已上传' },
      processing: { color: 'processing', text: '处理中' },
      completed: { color: 'success', text: '已完成' },
      failed: { color: 'error', text: '失败' },
    };
    const cfg = map[status] || { color: 'default', text: status };
    return <Tag color={cfg.color}>{cfg.text}</Tag>;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return '刚刚';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
    return date.toLocaleDateString('zh-CN') + ' ' + date.toLocaleTimeString('zh-CN');
  };

  const handleDownload = async (task) => {
    try {
      message.loading({ content: '正在下载...', key: 'download' });
      await downloadFile(task.task_id, task.filename);
      message.success({ content: '下载成功！', key: 'download' });
    } catch (error) {
      message.error({ content: '下载失败', key: 'download' });
    }
  };

  return (
    <div className="history-page">
      {/* 左侧统计面板 */}
      <div className="history-stats">
        <div
          className={`history-stat-card ${filter === null ? 'active' : ''}`}
          onClick={() => setFilter(null)}
        >
          <div className="history-stat-value">{stats.total}</div>
          <div className="history-stat-label">总任务数</div>
        </div>
        <div
          className={`history-stat-card ${filter === 'processing' ? 'active' : ''}`}
          onClick={() => setFilter(filter === 'processing' ? null : 'processing')}
        >
          <div className="history-stat-value" style={{ color: '#1890ff' }}>{stats.processing}</div>
          <div className="history-stat-label">运行中</div>
        </div>
        <div
          className={`history-stat-card ${filter === 'completed' ? 'active' : ''}`}
          onClick={() => setFilter(filter === 'completed' ? null : 'completed')}
        >
          <div className="history-stat-value" style={{ color: '#52c41a' }}>{stats.completed}</div>
          <div className="history-stat-label">已完成</div>
        </div>
        <div
          className={`history-stat-card ${filter === 'failed' ? 'active' : ''}`}
          onClick={() => setFilter(filter === 'failed' ? null : 'failed')}
        >
          <div className="history-stat-value" style={{ color: '#ff4d4f' }}>{stats.failed}</div>
          <div className="history-stat-label">失败</div>
        </div>

        <Button
          icon={<ReloadOutlined />}
          onClick={loadTasks}
          loading={loading}
          block
          style={{ marginTop: 8 }}
        >
          刷新
        </Button>
      </div>

      {/* 右侧任务列表 */}
      <div className="history-list">
        {loading && tasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Spin size="large" />
          </div>
        ) : filteredTasks.length === 0 ? (
          <Empty
            description={filter ? '没有匹配的任务' : '暂无任务历史'}
          />
        ) : (
          filteredTasks.map((task) => (
            <div key={task.task_id} className="history-task-card">
              <div className="history-task-header">
                <span className="history-task-filename">{task.filename}</span>
                {getStatusTag(task.status)}
              </div>
              <div className="history-task-meta">
                <span>
                  <ClockCircleOutlined style={{ marginRight: 4 }} />
                  {formatTime(task.created_at)}
                </span>
                {task.status === 'processing' && (
                  <span>进度: {task.progress}%</span>
                )}
              </div>
              {task.prompt && (
                <Paragraph
                  className="history-task-prompt"
                  ellipsis={{ rows: 2, expandable: true, symbol: '展开' }}
                >
                  <strong>Prompt:</strong> {task.prompt}
                </Paragraph>
              )}
              {task.status === 'completed' && (
                <div className="history-task-actions">
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownload(task)}
                  >
                    下载
                  </Button>
                  <Button
                    size="small"
                    icon={<BarChartOutlined />}
                    onClick={() => {
                      setAnalysisTaskId(task.task_id);
                      setShowAnalysisModal(true);
                    }}
                  >
                    数据分析
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* 数据分析弹窗 */}
      <DataAnalysisModal
        visible={showAnalysisModal}
        onClose={() => {
          setShowAnalysisModal(false);
          setAnalysisTaskId(null);
        }}
        taskId={analysisTaskId}
      />
    </div>
  );
};

export default HistoryPage;